// src/service/apiService.js
import axiosInstance from './axiosInstance';


// 设置请求头为 JSON 格式
axiosInstance.defaults.headers.common['Content-Type'] = 'application/json';

// 通用 GET 请求
export const getRequest = async (url, params = {}) => {
  try {
    const response = await axiosInstance.get(url, { params });
    return response.data;
  } catch (error) {
    console.error(`Error fetching data from ${url}`, error);
    throw error;
  }
};

// 通用 POST 请求,增加超时时间参数
export const postRequest = async (url, data = {}, timeout = 100000) => {
  try {
    const response = await axiosInstance.post(url, data, { timeout });
    return response.data;
  } catch (error) {
    console.error(`Error posting data to ${url}`, error);
    throw error;
  }
};

// 通用 PUT 请求
export const putRequest = async (url, data = {}) => {
  try {
    const response = await axiosInstance.put(url, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating data at ${url}`, error);
    throw error;
  }
};

// 通用 DELETE 请求，支持传递请求体
export const deleteRequest = async (url, data = null) => {
  try {
    const config = data ? { data } : undefined;
    const response = await axiosInstance.delete(url, config);
    return response.data;
  } catch (error) {
    console.error(`Error deleting data at ${url}`, error);
    throw error;
  }
};

// 通用 PATCH 请求
export const patchRequest = async (url, data = {}) => {
  try {
    const response = await axiosInstance.patch(url, data);
    return response.data;
  } catch (error) {
    console.error(`Error patching data at ${url}`, error);
    throw error;
  }
};

export const postStreamRequest = async (url, data, onChunk) => {
  
  try {
    // 创建流式请求配置
    const config = {
      method: 'post',
      url: url,
      data: data,
      headers: {
        'Content-Type': 'application/json',
        // 修改 Accept 头，主要接受 SSE 格式
        'Accept': 'text/event-stream'
      },
      // 关键：指定响应类型为流
      responseType: 'stream',
      // 为了保持一致性，我们使用与其他请求相同的超时时间
      timeout: 100000,
      // 添加自定义处理器，用于识别流响应
      streamHandler: true
    };

    // 使用 axiosInstance 发送请求
    const streamResponse = await axiosInstance.request(config);
  
    
    // 检查响应对象是否有效
    if (!streamResponse || !streamResponse.request) {
      throw new Error('服务器响应无效');
    }
    
    // 获取响应流
    const responseBody = streamResponse.request.body;
    
    
    // 创建读取器和解码器
    const reader = responseBody.getReader();
    const decoder = new TextDecoder();
    let result = '';
    let buffer = ''; // 用于存储不完整的SSE消息
    
    // 流式处理函数
    let isStreamDone = false;
    while (!isStreamDone) {
      const { done, value } = await reader.read();
      
      if (done) {
        isStreamDone = true;
        break;
      }
      
      // 解码当前数据块
      const chunk = decoder.decode(value, { stream: true });
      
      
      // 将新的块添加到缓冲区
      buffer += chunk;
      
      // 处理SSE格式的数据（可能包含多个完整/不完整的消息）
      // SSE消息格式: data: XXX\n\n 
      const messages = buffer.split('\n\n');
      
      // 最后一个元素可能是不完整的消息，所以保留在缓冲区
      buffer = messages.pop() || '';
      
      
      // 处理每个完整的消息
      for (const message of messages) {
        if (!message.trim()) continue;
        
        // 处理SSE消息格式
        const lines = message.split('\n').filter(line => line.trim());
        
        for (const line of lines) {
          // 跳过注释和空行
          if (line.startsWith(':') || !line.trim()) {
            continue;
          }
          
          // 处理以"data:"开头的行
          if (line.startsWith('data:')) {
            const content = line.substring(5).trim();
            
            if (content === '[DONE]') {
              continue;
            }
            
            // 直接处理内容，不使用缓冲区
            if (content && content !== '[DONE]') {
              
              // 将内容添加到结果
              result += content;
              
              // 执行回调函数
              if (onChunk && typeof onChunk === 'function') {
                onChunk(content);
              }
            }
          }
        }
      }
    }
    
    // 处理剩余的缓冲区内容
    if (buffer.trim()) {
      
      const lines = buffer.split('\n').filter(line => line.trim());
      for (const line of lines) {
        if (line.startsWith('data:')) {
          const content = line.substring(5).trim();
          if (content && content !== '[DONE]') {
            
            result += content;
            if (onChunk && typeof onChunk === 'function') {
              onChunk(content);
            }
          }
        } else if (line.trim() && line !== '[DONE]') {
          
          result += line;
          if (onChunk && typeof onChunk === 'function') {
            onChunk(line);
          }
        }
      }
    }
    
    
    return result;
  } catch (error) {
    console.error(`流请求失败: ${url}`);
    throw error;
  }
};

/**
 * 备用方案：当流式请求失败时使用标准 JSON 请求
 * @param {string} url - 接口地址
 * @param {Object} data - 请求数据
 * @param {Function} onChunk - 处理接收到的数据块的回调函数
 * @returns {Promise<string>} - 请求结果
 */
export const postFallbackRequest = async (url, data, onChunk) => {
  
  try {
    // 先尝试使用流式请求
    try {
      const result = await postStreamRequest(url, data, onChunk);
      return result;
    } catch (streamError) {
      console.warn(`流式请求失败，切换到标准请求: ${url}`, { 
        error: streamError.message
      });
    }
  } catch (error) {
    console.error(`回退请求失败: ${url}`, { 
      error: error.message,
      stack: error.stack
    });
    throw error;
  }
};

export default {
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
  patchRequest,
  postStreamRequest,
  postFallbackRequest,
};
